import { useGetMachineAgentId } from '#~/agent/components/bridge/hooks.js';
import { useAgents } from '#~/agent/hooks.js';
import { fieldKeyedAgentsEqual, idNamePick } from '#~/agent/provider/use-agents-field-eq.js';
import {
  IrisDeviceCard,
  type IrisDeviceCardSource
} from '#~/device/components/iris-device-card/index.js';
import { PostHogFeatureFlag, useFeatureFlag } from '#~/posthog/index.js';
import * as Select from '@studio/design-system/select';
import { Spinner } from '@studio/design-system/spinner';

import type { Agent } from '@studio/models/agent';
import type { Discovery } from '#~/device/discovery/index.js';
import { useLatestFn } from '@studio/react-utils/hooks';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { twMerge } from 'cn';
import { Context } from '../store/context.js';
import { useProducer, useStore } from '../store/hooks.js';

export interface BridgeSelectorProps {
  className?: string;
  onSelect?: (agentId: Agent['id']) => void;
}
export const BridgeSelector = ({ className }: BridgeSelectorProps) => {
  const { enabled: isBridgeEnabled } = useFeatureFlag(PostHogFeatureFlag.BRIDGE_FEATURE, {
    defaultValue: false
  });

  const producer = useProducer();
  const store = useContext(Context);
  const selectedAgentId = useStore((µ) => µ.agentId);
  const roomAgentIds = useStore((µ) => µ.agentIds);
  const sourceSelection = useStore((µ) => µ.source.selection);
  const pendingSelection = useRef<{ deviceId: Discovery['id'] } | undefined>(undefined);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    // Observe each choice, including select→deselect within one render batch.
    const unsubscribe = store?.subscribe((state, previous) => {
      if (
        state.source.preferredId !== undefined &&
        state.source.preferredId !== previous.source.preferredId
      ) {
        pendingSelection.current = undefined;
      }
    });
    return () => {
      mounted.current = false;
      unsubscribe?.();
    };
  }, [store]);

  const { agentId: machineAgentId, loading } = useGetMachineAgentId();

  // Get all agents to look up their names
  const agents = useAgents(
    (a) => a.filter((a) => !a.embedded).map((a) => ({ id: a.id, name: a.name })),
    (left, right) => fieldKeyedAgentsEqual(left, right, idNamePick)
  );

  // Get agents that can discover this source
  const availableAgents = useMemo(() => {
    // Map to full agent objects with correct names, but only for agents
    // that actually have this source in their discoveries
    return agents.filter((agent) => sourceSelection?.agentIds?.includes(agent.id));
  }, [agents, sourceSelection]);

  const irisDeviceCardSource: IrisDeviceCardSource | undefined = useMemo(() => {
    if (!sourceSelection) {
      return undefined;
    }

    return {
      name: sourceSelection.name,
      ...(sourceSelection.modelId && { modelId: sourceSelection.modelId }),
      ...(sourceSelection.version && { version: sourceSelection.version })
    };
  }, [sourceSelection?.modelId, sourceSelection?.name, sourceSelection?.version]);

  const safeSelectedAgentId = useMemo(() => {
    if (availableAgents.find((a) => a.id === selectedAgentId)) {
      return selectedAgentId;
    }

    return availableAgents[0]?.id;
  }, [availableAgents, selectedAgentId]);

  useEffect(() => {
    if (machineAgentId && !loading && roomAgentIds.includes(machineAgentId)) {
      onSelectBridge(machineAgentId);
    }
  }, [machineAgentId, loading, roomAgentIds]);

  const onSelectBridge = useLatestFn(async (nextId: Agent['id']): Promise<void> => {
    let reset: typeof pendingSelection.current;

    producer((draft) => {
      // Metadata refreshes can repeat the current Bridge. They must not
      // interrupt the preview or schedule restoration of an older camera.
      if (draft.agentId === nextId) return;
      draft.agentId = nextId;

      const deviceId = draft.source.preferredId ?? pendingSelection.current?.deviceId;
      if (!deviceId) return;
      reset = { deviceId };
      // Keep the existing preview teardown when changing Bridges. A second
      // Bridge choice carries the pending camera forward without an old timer winning.
      draft.source.preferredId = undefined;
    });

    if (!reset) return;
    const request = reset;
    pendingSelection.current = request;
    await new Promise((resolve) => setTimeout(resolve, 250));

    if (!mounted.current || pendingSelection.current !== request) return;
    pendingSelection.current = undefined;
    producer((draft) => {
      // Camera clicks and bulk selection take precedence over delayed preview
      // reset completion; closing the dialog also invalidates the callback above.
      if (
        draft.agentId === nextId &&
        !draft.source.bulkMode &&
        draft.source.preferredId === undefined
      ) {
        draft.source.preferredId = request.deviceId;
      }
    });
  });

  if (loading) {
    return <Spinner />;
  }

  if (!sourceSelection) {
    return null;
  }

  if (sourceSelection?.irisEnabled) {
    return (
      <div className='max-w-4xl mx-auto mt-12 p-6'>
        <div
          className='relative overflow-hidden rounded-4'
          style={{
            background: 'linear-gradient(to right, #FF6384, #36A2EB, #4BC0C0, #FF9F40) border-box',
            border: '1px solid transparent'
          }}
        >
          <div className='rounded-3'>
            <IrisDeviceCard
              sourceSelection={irisDeviceCardSource}
              size='large'
              variant='bridge-selector'
            />
          </div>
        </div>
      </div>
    );
  }

  return isBridgeEnabled ? (
    <div className={twMerge('mb-2', className)}>
      <p
        className={twMerge(
          'mt-12 font-medium',
          availableAgents.length === 1 ? 'text-muted' : 'text-light'
        )}
      >
        Bridge Selection
      </p>
      <p
        className={twMerge(
          'mb-12 text-sm font-regular',
          availableAgents.length === 1 ? 'text-muted' : ''
        )}
      >
        This is the Bridge that will be processing media and managing control for this device.
      </p>
      <Select.Root
        onValueChange={onSelectBridge}
        value={safeSelectedAgentId}
        disabled={availableAgents.length === 1 || loading}
      >
        <Select.Trigger className='w-[438px] bg-base-100'>
          <Select.Value placeholder='Select a bridge' />
        </Select.Trigger>

        <Select.Content>
          {availableAgents.map((agent) => (
            <Select.Item key={agent.id} value={agent.id} className='text-muted'>
              {agent.name}
            </Select.Item>
          ))}
        </Select.Content>
      </Select.Root>
    </div>
  ) : null;
};

export const ManualSourceBridgeSelector = ({ className, onSelect }: BridgeSelectorProps) => {
  const { enabled: isBridgeEnabled } = useFeatureFlag(PostHogFeatureFlag.BRIDGE_FEATURE, {
    defaultValue: false
  });

  const producer = useProducer();
  const selectedAgentId = useStore((µ) => µ.agentId);

  // Get all agents to look up their names
  const agents = useAgents(
    (a) => a.filter((a) => !a.embedded).map((a) => ({ id: a.id, name: a.name })),
    (left, right) => fieldKeyedAgentsEqual(left, right, idNamePick)
  );

  const onSelectBridge = useLatestFn(async (nextId: Agent['id']): Promise<void> => {
    producer((draft) => {
      draft.agentId = nextId;
    });

    onSelect?.(nextId);
  });

  // Hide bridge selector if feature flag is disabled
  if (!isBridgeEnabled) {
    return null;
  }

  return (
    <div className={twMerge('mb-2', className)}>
      <Select.Root
        onValueChange={onSelectBridge}
        value={selectedAgentId}
        disabled={agents.length === 1}
      >
        <Select.Trigger className='w-[438px] bg-base-100'>
          <Select.Value placeholder='Select a bridge' />
        </Select.Trigger>

        <Select.Content>
          {agents.map((agent) => (
            <Select.Item key={agent.id} value={agent.id} className='text-muted'>
              {agent.name}
            </Select.Item>
          ))}
        </Select.Content>
      </Select.Root>
    </div>
  );
};
